// The tile strip (ENTRAID-347 redesign, brand/onguard/README.md "Charts"): one small square per day or per item, the
// IPC tile language (square, unrounded). Two variants:
//   fill    (default) solid squares: sky for done, track for not done, the warn dot for partial and bad only for a
//           failed item;
//   status  one white square with a blue border per source (the product owner's "Collections today" look): a green
//           check when collected, empty when pending, a red X when failed. TileLegend names the three states.
// The strip is decoration (aria-hidden): the caller always states the count, and any failed or partial item, in words
// beside it. `labels` (one per tile) become hover tooltips (title); they are not focusable, because the strip is hidden
// from assistive technology and usually sits inside a card link.

/** Fill per tile state (CSS tokens in src/styles.css). */
export const TILE_FILL = Object.freeze({
  on: 'var(--sky)',
  off: 'var(--track)',
  // A status strip (Fleet "Active": one tile per customer) uses the status tokens: good, warn, bad.
  good: 'var(--good)',
  warn: 'var(--warn-dot)',
  bad: 'var(--bad)',
});

/** The states of the status variant, in legend order. */
export const STATUS_STATES = Object.freeze(['collected', 'pending', 'failed']);

const STATUS_WORD = Object.freeze({ collected: 'Collected', pending: 'Pending', failed: 'Failed' });

/** One status square: white, blue border, and a glyph for collected (check) or failed (X). */
export function StatusTile({ status, title, size = 'h-4 w-4' }) {
  const s = STATUS_STATES.includes(status) ? status : 'pending';
  return (
    <span
      className={`flex shrink-0 items-center justify-center bg-white ${size}`}
      data-tile={s}
      title={title}
      style={{ border: '1.5px solid var(--blue)' }}
    >
      {s === 'collected' ? (
        <svg viewBox="0 0 12 12" className="h-full w-full" focusable="false" aria-hidden="true">
          <path d="M2.5 6.3 5 8.7 9.6 3.4" fill="none" stroke="var(--good)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
      {s === 'failed' ? (
        <svg viewBox="0 0 12 12" className="h-full w-full" focusable="false" aria-hidden="true">
          <path d="M3.4 3.4 8.6 8.6M8.6 3.4 3.4 8.6" fill="none" stroke="var(--bad)" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ) : null}
    </span>
  );
}

/**
 * @param {{ states: string[], variant?: 'fill'|'status', labels?: string[], className?: string, testId?: string }} props
 * Other props (data attributes) pass to the strip element.
 */
export default function TileStrip({ states, variant = 'fill', labels, className = '', testId, ...rest }) {
  if (!Array.isArray(states) || states.length === 0) return null;
  const title = (i) => (Array.isArray(labels) && typeof labels[i] === 'string' ? labels[i] : undefined);
  return (
    <div
      className={`flex flex-wrap gap-1 ${className}`.trim()}
      aria-hidden="true"
      data-tile-strip=""
      data-tile-variant={variant}
      data-testid={testId}
      {...rest}
    >
      {variant === 'status'
        ? states.map((s, i) => <StatusTile key={i} status={s} title={title(i)} />)
        : states.map((s, i) => (
          <span key={i} className="block h-3 w-3" data-tile={s} title={title(i)} style={{ background: TILE_FILL[s] ?? TILE_FILL.off }} />
        ))}
    </div>
  );
}

/** "30 collected, 3 pending, 1 failed": the counts in words, for screen readers and captions. */
export function statusSentence(counts) {
  return STATUS_STATES.map((s) => `${counts?.[s] ?? 0} ${s}`).join(', ');
}

/**
 * The legend under a status strip: a sample square, the state word and its count, in ink. The visible row is
 * aria-hidden; a visually hidden sentence carries the same counts once.
 *
 * @param {{ counts: { collected?: number, pending?: number, failed?: number }, className?: string }} props
 */
export function TileLegend({ counts, className = '' }) {
  return (
    <div className={className} data-tile-legend="">
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ipc-ink" aria-hidden="true">
        {STATUS_STATES.map((s) => (
          <li key={s} className="flex items-center gap-1.5" data-legend={s}>
            <StatusTile status={s} size="h-3.5 w-3.5" />
            <span>{STATUS_WORD[s]}</span>
            <span className="font-semibold tabular-nums">{counts?.[s] ?? 0}</span>
          </li>
        ))}
      </ul>
      <p className="sr-only">{`Sources: ${statusSentence(counts)}.`}</p>
    </div>
  );
}
